// src/pages/candidate/CandidateInterviews.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import {
  VideoCameraIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const CandidateInterviews = () => {
  const { data: interviews, isLoading, error } = useQuery({
    queryKey: ['candidate-interviews'],
    queryFn: () => apiService.getCandidateInterviews(),
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return ClockIcon;
      case 'in_progress':
        return VideoCameraIcon;
      case 'completed':
        return CheckCircleIcon;
      default:
        return ClockIcon;
    }
  };

  const getActionButton = (interview) => {
    switch (interview.session_status) {
      case 'scheduled':
        return (
          <Link
            to={`/interview/${interview.id}`}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlayIcon className="h-4 w-4 mr-1" />
            Start Interview
          </Link>
        );
      case 'in_progress':
        return (
          <Link
            to={`/interview/${interview.id}`}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <VideoCameraIcon className="h-4 w-4 mr-1" />
            Continue
          </Link>
        );
      case 'completed':
        return (
          <button
            disabled
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-500 bg-gray-100 cursor-not-allowed"
          >
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Completed
          </button>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load interviews. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Interviews</h1>
        <p className="text-gray-600">Track your interview progress and start new interviews.</p>
      </div>

      {/* Interview List */}
      {interviews && interviews.length > 0 ? (
        <div className="space-y-4">
          {interviews.map((interview) => {
            const StatusIcon = getStatusIcon(interview.session_status);
            
            return (
              <div key={interview.id} className="card hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <VideoCameraIcon className="h-6 w-6 text-primary-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {interview.title}
                      </h3>
                      {interview.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {interview.description}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {interview.duration_minutes} minutes
                        </div>
                        
                        {interview.scheduled_at && (
                          <div className="flex items-center text-sm text-gray-500">
                            <span>Scheduled: {format(new Date(interview.scheduled_at), 'MMM dd, yyyy')}</span>
                          </div>
                        )}
                        
                        {interview.started_at && (
                          <div className="flex items-center text-sm text-gray-500">
                            <span>Started: {format(new Date(interview.started_at), 'MMM dd, yyyy HH:mm')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(interview.session_status)}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {interview.session_status.replace('_', ' ').toUpperCase()}
                    </span>
                    
                    {getActionButton(interview)}
                  </div>
                </div>
                
                {/* Progress Bar for In Progress Interviews */}
                {interview.session_status === 'in_progress' && interview.progress_percentage !== undefined && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Progress</span>
                      <span className="text-sm text-gray-600">{Math.round(interview.progress_percentage)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${interview.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <VideoCameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No interviews yet</h3>
          <p className="text-gray-600 mb-6">
            You don't have any interviews scheduled. Check back later or contact your recruiter.
          </p>
        </div>
      )}

      {/* Interview Guidelines */}
      <div className="card bg-blue-50 border border-blue-200">
        <h3 className="text-lg font-medium text-blue-900 mb-3">Interview Guidelines</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <div className="flex items-start">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p>Ensure you have a stable internet connection and a quiet environment.</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p>Test your microphone and camera before starting the interview.</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p>Speak clearly and take your time to provide thoughtful answers.</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p>You can type notes in the text area to help with transcription accuracy.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateInterviews;